import { Cylinder, Line, Sphere } from '@react-three/drei';
import { colors } from '@ui';
import * as THREE from 'three';

import { getKeypointId } from '../helpers';

import { memo } from 'react';
import type { FrameFirstSkeleton } from '../skeletonUtils';
import type { KeypointIdToName } from '../types';

type DisplacementVisualizationProps = {
    skeleton: FrameFirstSkeleton;
    frame: number;
    referenceFrame?: number;
    targetPoints: string[];
    keypointIdToName: KeypointIdToName;
    currentFocus: { measurement: string; axis: number } | null;
    opacity?: number;
};

// Keypoints that use horizontal (Y-lateral) line rotation for Lift (Z) axis
const LIFT_HORIZONTAL_KEYPOINTS = new Set(['head', 'upper_torso', 'pelvis']);

export function DisplacementVisualization({
    skeleton,
    frame = 0,
    referenceFrame = 0,
    targetPoints,
    keypointIdToName,
    currentFocus,
    opacity = 1,
}: DisplacementVisualizationProps) {
    const keypointName = targetPoints[0];
    const keypointId = getKeypointId(keypointName, keypointIdToName);

    const point1 = skeleton[referenceFrame]?.[keypointId] ?? skeleton[skeleton.length - 1][keypointId];
    const point2 = skeleton[frame]?.[keypointId] ?? skeleton[skeleton.length - 1][keypointId];

    const radius = 0.03;
    const direction = new THREE.Vector3()
        .subVectors(new THREE.Vector3(...point2), new THREE.Vector3(...point1))
        .normalize();

    const lineStart = new THREE.Vector3().addVectors(
        new THREE.Vector3(...point1),
        direction.clone().multiplyScalar(radius),
    );
    const lineEnd = new THREE.Vector3().subVectors(
        new THREE.Vector3(...point2),
        direction.clone().multiplyScalar(radius),
    );

    const discRadius = 0.004;
    const discThickness = 12;
    const discSegments = 64;

    const focusedAxis = currentFocus?.axis;
    const drawAxisLines = typeof focusedAxis === 'number';

    const liftRotation: [number, number, number] = LIFT_HORIZONTAL_KEYPOINTS.has(keypointName)
        ? [0, Math.PI / 2, 0] // Horizontal (Y-lateral) for whitelisted keypoints
        : [Math.PI / 2, 0, Math.PI / 2]; // Default

    const rotations: [number, number, number][] = [
        [Math.PI / 2, 0, 0], // Thrust (X)
        [0, Math.PI / 2, Math.PI / 2], // Sway (Y)
        liftRotation, // Lift (Z)
    ];

    const rotation: [number, number, number] = drawAxisLines ? rotations[focusedAxis] : [0, 0, 0];

    return (
        <>
            <Line
                color={colors.yellow[500]}
                lineWidth={2}
                dashed
                castShadow={false}
                receiveShadow={false}
                segments={false}
                dashScale={64}
                points={[lineStart, lineEnd]}
                transparent
                opacity={opacity}
            />

            <Sphere castShadow={false} receiveShadow={false} position={point1} args={[0.032, 16, 16]}>
                <meshBasicMaterial color={colors.yellow[500]} transparent opacity={0.75 * opacity} />
            </Sphere>

            {drawAxisLines && (
                <>
                    <Cylinder
                        position={point1}
                        rotation={rotation}
                        castShadow={false}
                        receiveShadow={false}
                        args={[discRadius, discRadius, discThickness, discSegments]}
                    >
                        <meshBasicMaterial color={colors.yellow[500]} side={THREE.DoubleSide} opacity={opacity} />
                    </Cylinder>

                    <Cylinder
                        position={point2}
                        rotation={rotation}
                        castShadow={false}
                        receiveShadow={false}
                        args={[discRadius, discRadius, discThickness, discSegments]}
                    >
                        <meshBasicMaterial color={colors.green[500]} side={THREE.DoubleSide} opacity={opacity} />
                    </Cylinder>
                </>
            )}
        </>
    );
}

export const MemoDisplacementVisualization = memo(DisplacementVisualization);
